// Global error boundary — logs uncaught errors to audit_log
window.addEventListener('error', e => {
  console.error('FinTrack error:', e.message, e.filename, e.lineno);
  try {
    const sb = window.__ft_sb;
    const cu = window.__ft_cu;
    if (sb && cu) sb.rpc('log_audit', { p_user_id: cu.id, p_action: 'js_error', p_entity_type: 'error', p_entity_name: e.message?.substring(0, 200) || 'unknown' }).then(null, e => console.error('SBIS:', e));
  } catch (_) {}
});

window.addEventListener('unhandledrejection', e => {
  console.error('FinTrack promise rejection:', e.reason);
  try {
    const sb = window.__ft_sb;
    const cu = window.__ft_cu;
    if (sb && cu) sb.rpc('log_audit', { p_user_id: cu.id, p_action: 'promise_error', p_entity_type: 'error', p_entity_name: String(e.reason)?.substring(0, 200) || 'unknown' }).then(null, e => console.error('SBIS:', e));
  } catch (_) {}
});

// FinTrack — Entry Point
import '../css/styles.css';
import { initParticles, svgIcon, toggleSidebar, openEditModal } from './ui.js';
import { registerRenderer, showPage, startKeepAlive, applyDatePreset, fetchAutoKurs, setUnsavedData } from './nav.js';
import { doLogin, doLogout, initSessionCheck, changeOwnPassword } from './auth.js';
import { clMdl, toast } from './ui.js';
import { debounce } from './utils.js';
import { sb } from './supabase.js';
import { renderVrste, showAddVrsta, renderKom, showAddKom, saveKom, saveVrsta, showAddUser, saveUser, quickAssign, saveQuickAssign, handleKomImport, showMergeKom, backupData, restoreData, renderUsers } from './crud.js';
import { genReport, handleFile, parseCurrentSheet, filterReportDisplay, showCompare, tgGrp, initReportUpzone, saveReportToDb, clearReportData } from './report.js';
import { genKasn, filterKasn, expKasnXlsx, handleFileK, parseSheetK, initKasnUpzone, saveKasnToDb } from './kasnjenja.js';
import { expXlsx, expCsv, expPdf } from './export.js';
import { loadHistory, saveToHistory, showHistTab } from './history.js';
import { renderKontniPlan, handleKPFile, showAddKonto, saveKonto, expKontniPlan, loadKontniPlan, toggleKpSelectAll, batchDeleteKonta } from './kontni-plan.js';
import { handlePdfFiles, startPdfParsing, updateKjBtnState, expKjXlsx, toggleKursInput, openFixMap, fmSelectFromKP, saveFixMap, initKnjizenja, showKjView, openSaveIzvodModal, confirmSaveIzvod, clearKjData, expSavedIzvodXlsx } from './knjizenja.js';
import { renderDashboard } from './dashboard.js';
import { loadKomitentiFull, handleKfImport, debounceRenderKF, expKfQuickBooks, showAddKomitentFull } from './komitenti-full.js';
import { loadPocetnoStanje, handlePsImport, debounceRenderPS, expPsQuickBooks, addPsEntry, deleteSelectedPs, togglePsKontoDrop, filterPsKontoDrop } from './pocetno-stanje.js';
import { initZakljucniList, generateZl, expZlXlsx, filterZl } from './zakljucni-list.js';
import { loadKursPage } from './kurs.js';
import { initDatePickers } from './datepicker.js';
import { initCashflow, generateCashflow, saveCashflow, exportCashflowXlsx } from './cashflow.js';
import { initKontoSearch, searchKonto, exportKontoXlsx, toggleAnalDropdown, toggleKontoDropdown, filterKontoDropdown, clearKontoFilters } from './konto-search.js';
import { generateBS, exportBsXlsx, exportInvXlsx, exportPlXlsx, initBsSelectors, exportMesecniXlsx, exportCashflowYearXlsx, generateBS12, exportBs12Xlsx, generatePl12, exportPl12Xlsx, generateInv12, exportInv12Xlsx } from './balance-sheet.js';

// Expose for error boundary
window.__ft_sb = sb;

// Register renderers for nav.js (avoids circular imports)
registerRenderer('renderKom', renderKom);
registerRenderer('renderVrste', renderVrste);
registerRenderer('renderUsers', renderUsers);
registerRenderer('loadHistory', loadHistory);
registerRenderer('renderKontniPlan', renderKontniPlan);
registerRenderer('renderDashboard', renderDashboard);
registerRenderer('updateKjBtnState', updateKjBtnState);
registerRenderer('loadKontniPlan', loadKontniPlan);
registerRenderer('loadKomitentiFull', loadKomitentiFull);
registerRenderer('loadPocetnoStanje', loadPocetnoStanje);
registerRenderer('loadZakljucniList', initZakljucniList);
registerRenderer('loadKursPage', loadKursPage);

// Initialize modules
initParticles();
initReportUpzone();
initKasnUpzone();
initKnjizenja();
initDatePickers();
initCashflow();
initKontoSearch();
initBsSelectors('bs');
initBsSelectors('inv');
initBsSelectors('pl');
initBsSelectors('bs12');
initBsSelectors('pl12');
initBsSelectors('inv12');
startKeepAlive();

// Debounced functions
const debounceRenderKom = debounce(renderKom, 300);
const debounceRenderKP = debounce(renderKontniPlan, 300);

// Assign ALL inline-handler functions to window
Object.assign(window, {
  doLogin, doLogout, changeOwnPassword,
  handleFile, parseCurrentSheet, genReport, filterReportDisplay, showCompare, tgGrp, saveReportToDb, clearReportData,
  handleFileK, parseSheetK, genKasn, filterKasn, expKasnXlsx, saveKasnToDb,
  generateCashflow, saveCashflow, exportCashflowXlsx,
  searchKonto, exportKontoXlsx, toggleAnalDropdown, toggleKontoDropdown, filterKontoDropdown, clearKontoFilters,
  generateBS, exportBsXlsx, exportInvXlsx, exportPlXlsx, showMesTab,
  exportMesecniXlsx, exportCashflowYearXlsx, generateBS12, exportBs12Xlsx, generatePl12, exportPl12Xlsx,
  generateInv12, exportInv12Xlsx,
  expXlsx, expCsv, expPdf,
  saveToHistory,
  showAddKom, saveKom, showAddVrsta, saveVrsta, showAddUser, saveUser,
  quickAssign, saveQuickAssign,
  handleKomImport, showMergeKom, backupData, restoreData,
  handleKPFile, showAddKonto, saveKonto, expKontniPlan,
  toggleKpSelectAll, batchDeleteKonta,
  handlePdfFiles, startPdfParsing, expKjXlsx, toggleKursInput,
  openFixMap, fmSelectFromKP, saveFixMap,
  showKjView, openSaveIzvodModal, confirmSaveIzvod,
  clearKjData, expSavedIzvodXlsx,
  clMdl, toast,
  applyDatePreset, fetchAutoKurs,
  debounceRenderKom, debounceRenderKP,
  handleKfImport, debounceRenderKF, expKfQuickBooks, showAddKomitentFull,
  handlePsImport, debounceRenderPS, expPsQuickBooks,
  addPsEntry, deleteSelectedPs, togglePsKontoDrop, filterPsKontoDrop,
  generateZl, expZlXlsx, filterZl,
  updateKjBtnState,
  toggleTheme, toggleSidebar, openEditModal,
  setUnsavedData, showHistTab, renderDashboard
});

// Mesečni tab switching
function showMesTab(tab) {
  const boxes = { cf: 'mesCfBox', bs: 'mesBsBox', inv: 'mesInvBox', pl: 'mesPlBox', bs12: 'mesBs12Box', pl12: 'mesPl12Box', inv12: 'mesInv12Box' };
  const btns = { cf: 'btnMesCf', bs: 'btnMesBs', inv: 'btnMesInv', pl: 'btnMesPl', bs12: 'btnMesBs12', pl12: 'btnMesPl12', inv12: 'btnMesInv12' };
  Object.entries(boxes).forEach(([k, id]) => { const el = document.getElementById(id); if (el) el.style.display = k === tab ? '' : 'none'; });
  Object.entries(btns).forEach(([k, id]) => { const el = document.getElementById(id); if (el) el.classList.toggle('active', k === tab); });
}
window.showMesTab = showMesTab;

// Nedostaje modal — gap analysis vs original Excel
function showMesecniGaps() {
  const mdl = document.getElementById('mdlEdit');
  document.getElementById('meTitle').textContent = 'Nedostaje u aplikaciji';
  const body = document.getElementById('meBody');
  body.textContent = '';
  body.style.cssText = 'max-height:70vh;overflow-y:auto;padding-right:8px';

  // ── SEKCIJA 1: Nedostaje ──
  const nedHead = document.createElement('div'); nedHead.style.cssText = 'padding:10px 0 6px;font-weight:700;font-size:16px;color:var(--dng);border-bottom:2px solid var(--dng);margin-bottom:4px';
  nedHead.textContent = 'Nedostaje'; body.appendChild(nedHead);

  const nedItems = [
    { cat: 'P&L 12 Months', items: [
      'Panels Produced (kom) — proizvodne metrike',
      '% Growth mesec-na-mesec za proizvodnju',
      'Panels Rejected (Scrap) — škart',
      'Panels Sold — prodati paneli',
      'Forecast vs Real kolone',
    ]},
    { cat: 'Cashflow', items: [
      'Intercompany Loan — koji konto koristiti?',
    ]},
  ];

  nedItems.forEach(section => {
    const sh = document.createElement('div'); sh.style.cssText = 'padding:10px 0 4px;font-weight:700;font-size:14px;color:var(--acc);border-bottom:1px solid var(--border);margin-top:6px;display:flex;justify-content:space-between';
    sh.textContent = section.cat;
    const badge = document.createElement('span');
    badge.style.cssText = 'font-size:11px;padding:2px 8px;border-radius:10px;font-weight:600;background:var(--dng-bg);color:var(--dng)';
    badge.textContent = section.items.length;
    sh.appendChild(badge); body.appendChild(sh);
    section.items.forEach(text => {
      const row = document.createElement('div'); row.style.cssText = 'padding:6px 0 6px 8px;font-size:13px;display:flex;align-items:center;gap:8px;color:var(--tx2)';
      row.appendChild(Object.assign(document.createElement('span'), { textContent: '\u2B1C' }));
      row.appendChild(Object.assign(document.createElement('span'), { textContent: text }));
      body.appendChild(row);
    });
  });

  // ── SEKCIJA 2: Za razjasniti ──
  const qHead = document.createElement('div'); qHead.style.cssText = 'padding:10px 0 6px;font-weight:700;font-size:16px;color:var(--warn);border-bottom:2px solid var(--warn);margin-top:20px;margin-bottom:4px';
  qHead.textContent = 'Za razjasniti'; body.appendChild(qHead);

  const questions = [
    { cat: 'Cashflow', items: [
      'POREZI U CASHFLOW-U: Na dnu cashflow izveštaja postoje stavke "Monthly VAT" i "Retained VAT". Trenutno se oba broja sabiraju sa Final Balance da bi se dobio NET CASH. Pitanje: da li porezi treba da SMANJUJU cash (oduzimanje) ili da se DODAJU kao poreska pozicija? Potrebno uporediti sa originalnim Excel cashflow izveštajem.',
      'KATEGORIJA "OTHERS": U rashodima cashflow-a postoji stavka "Others" koja predstavlja sve odlive sa bankovnog računa koji nisu mapirani na poznate kategorije (dobavljači, plate, porezi itd). Ako je ovaj broj prevelik ili netačan, znači da neki rashodi nisu pravilno kategorisani — potrebno proveriti za svaki mesec.',
      'INTERCOMPANY LOAN: U originalnom Excel-u postoji stavka za međukompanijski zajam. Koji konto se koristi za ovu transakciju? Bez tog podatka ne možemo dodati ovu liniju u cashflow.',
      'KONTO 2418 (INTERCOMPANY BANKA): U knjiženjima postoji konto 2418 sa prometom od 3.84M RSD. Trenutno je isključen iz cashflow kalkulacije jer predstavlja intercompany transfer, ne operativni odliv. Da li je ovo tačno? Da li treba posebnu liniju u cashflow-u za intercompany transfere?',
    ]},
    { cat: 'BS 12 Months (Bilans)', items: [
      'UPISANI NEUPLAĆENI KAPITAL (konto 00): Ovaj konto predstavlja kapital koji su vlasnici upisali ali još nisu uplatili. Trenutno je svrstan u "Other Current Assets" (tekuća aktiva). Da li je to tačno, ili treba da bude prikazan kao posebna linija u sekciji Equity (kapital)?',
      'INTERCOMPANY POTRAŽIVANJA (konto 200-203): U bilansu postoji linija "Account Receivables Interco." za međukompanijska potraživanja. Da li firma ECOR Serbia koristi ove konte za potraživanja od povezanih kompanija, ili su prazni i linija može da se ukloni?',
    ]},
    { cat: 'P&L 12 Months (Bilans uspeha)', items: [
      'PROIZVODNI PODACI: Originalni Excel ima podatke o broju proizvedenih panela, škarta i prodatih panela (Panels Produced, Rejected, Sold). Ovi podaci ne postoje u knjiženjima. Da li se vode negde drugde (ERP, proizvodni sistem, Excel tabela) odakle bi mogli da se uvoze?',
      'FORECAST KOLONE: Originalni Excel ima kolone "Forecast" pored "Real" vrednosti za svaki mesec. Da li se planira ručni unos planiranih (budžetskih) vrednosti, ili ova funkcionalnost nije potrebna?',
    ]},
    { cat: 'P&L / Knjiženja', items: [
      'PRIHODI (KLASA 6): U bazi trenutno NE POSTOJE knjiženja klase 6 (prihodi od prodaje, finansijski prihodi itd). To znači da P&L 12 Months prikazuje 0 za sve prihode. Da li su prihodi knjiženi u drugom sistemu ili tek treba da se unesu?',
    ]},
    { cat: 'Knjiženja', items: [
      'EDITOVANJE POSLE ČUVANJA: Kada se izvod sačuva u bazu, pojedinačne stavke se ne mogu menjati — mora se obrisati ceo izvod i ponovo učitati. Da li je potrebna mogućnost editovanja pojedinačnih stavki posle čuvanja?',
      'CASHFLOW "OTHERS": U rashodima cashflow-a postoji kategorija "Others" koja prikazuje sve nerazvrstanog odlive kao jedan broj bez detalja. Da li je potreban pregled šta tačno ulazi u "Others" (drill-down)?',
    ]},
    { cat: 'Početno stanje', items: [
      'ZATVARANJE PERIODA: Funkcija "Zatvori period" prebacuje knjiženja u početno stanje. Kada se ovo radi — jednom godišnje (na kraju fiskalne godine) ili mesečno? Od ovog odgovora zavisi kako aplikacija računa stanje za svaki mesec.',
    ]},
    { cat: 'Dashboard', items: [
      'TOP 10 DOBAVLJAČI/KUPCI: Trenutno prikazuje nazive KONTA (npr. "Dobavljači u zemlji"), a ne stvarna imena firmi. Da bi prikazivao imena firmi, potrebni su podaci na nivou analitike. Da li je to potrebno?',
    ]},
    { cat: 'Izveštaji', items: [
      'ZASTARELOST SAČUVANIH IZVEŠTAJA: Sačuvani izveštaji u Istoriji su snimci iz trenutka čuvanja. Ako se podaci promene (ispravke, novi izvodi), sačuvani izveštaj se ne ažurira. Da li treba upozorenje o zastarelosti?',
    ]},
  ];

  questions.forEach(section => {
    const sh = document.createElement('div'); sh.style.cssText = 'padding:10px 0 4px;font-weight:700;font-size:14px;color:var(--acc);border-bottom:1px solid var(--border);margin-top:6px;display:flex;justify-content:space-between';
    sh.textContent = section.cat;
    const badge = document.createElement('span');
    badge.style.cssText = 'font-size:11px;padding:2px 8px;border-radius:10px;font-weight:600;background:var(--warn-bg);color:var(--warn)';
    badge.textContent = section.items.length;
    sh.appendChild(badge); body.appendChild(sh);
    section.items.forEach(text => {
      const row = document.createElement('div'); row.style.cssText = 'padding:6px 0 6px 8px;font-size:13px;display:flex;align-items:flex-start;gap:8px;color:var(--tx2)';
      row.appendChild(Object.assign(document.createElement('span'), { textContent: '\u2753', style: { cssText: 'flex-shrink:0' } }));
      row.appendChild(Object.assign(document.createElement('span'), { textContent: text }));
      body.appendChild(row);
    });
  });

  // Summary
  const totalNed = nedItems.reduce((s, sec) => s + sec.items.length, 0);
  const totalQ = questions.reduce((s, sec) => s + sec.items.length, 0);
  const summary = document.createElement('div'); summary.style.cssText = 'padding:14px 0 8px;font-size:13px;color:var(--tx3);border-top:2px solid var(--border);margin-top:16px;text-align:center';
  summary.textContent = 'Nedostaje: ' + totalNed + ' stavki  |  Za razjasniti: ' + totalQ + ' pitanja';
  body.appendChild(summary);

  const saveBtn = document.getElementById('meSave');
  const newSaveBtn = saveBtn.cloneNode(true); saveBtn.parentNode.replaceChild(newSaveBtn, saveBtn);
  newSaveBtn.id = 'meSave'; newSaveBtn.textContent = 'Zatvori'; newSaveBtn.onclick = () => clMdl('mdlEdit');
  mdl.classList.add('active');
}
window.showMesecniGaps = showMesecniGaps;

// Keyboard shortcuts
document.addEventListener('keydown', e => {
  if (e.key === 'Escape') { const m = document.querySelector('.mdl-ov.active'); if (m) m.classList.remove('active'); }
  if (e.key === 'Enter' && !e.shiftKey) { const m = document.querySelector('.mdl-ov.active'); if (m) { const btn = m.querySelector('.bsav'); if (btn && document.activeElement?.tagName !== 'TEXTAREA') { e.preventDefault(); btn.click(); } } }
});

// Theme toggle
function toggleTheme() {
  const current = document.documentElement.getAttribute('data-theme');
  const next = current === 'light' ? 'dark' : 'light';
  document.documentElement.setAttribute('data-theme', next);
  localStorage.setItem('ft_theme', next);
  const btn = document.getElementById('themeBtn2');
  if (btn) { btn.textContent = ''; btn.appendChild(svgIcon(next === 'light' ? 'sun' : 'moon', 16)); }
}
window.toggleTheme = toggleTheme;

// Restore theme from localStorage
const savedTheme = localStorage.getItem('ft_theme');
if (savedTheme) {
  document.documentElement.setAttribute('data-theme', savedTheme);
  const btn = document.getElementById('themeBtn2');
  if (btn) { btn.textContent = ''; btn.appendChild(svgIcon(savedTheme === 'light' ? 'sun' : 'moon', 16)); }
}

// Start session check (async — restores session if valid)
initSessionCheck();
